import { detectFormat, type LogFormat } from "./detect";
import { parseMjai } from "./parsers/mjai";
import { parseMjlog } from "./parsers/mjlog";
import { parseTenhou } from "./parsers/tenhou";
import type { ParseResult } from "./types";
import { validateEvents } from "./validate";

const PARSERS: Record<LogFormat, (text: string) => ParseResult> = {
    mjai: parseMjai,
    mjlog: parseMjlog,
    tenhou: parseTenhou,
};

const byId = <T extends HTMLElement>(id: string): T => {
    const el = document.getElementById(id);
    if (!el) throw new Error(`missing element #${id}`);
    return el as T;
};

const container = byId<HTMLElement>("viewer-container");
const fileInput = byId<HTMLInputElement>("file-input");
const pasteInput = byId<HTMLInputElement>("paste-input");
const rendererSelect = byId<HTMLSelectElement>("renderer-select");
const status = byId<HTMLElement>("status");
const errorBanner = byId<HTMLElement>("error");
const errorText = byId<HTMLElement>("error-text");
const dropOverlay = byId<HTMLElement>("drop-overlay");

let viewer: RiichiViewer | null = null;
let currentLog: MjaiEvent[] | null = null;

function showError(message: string): void {
    errorText.textContent = message;
    errorBanner.hidden = false;
}

function mountViewer(log: MjaiEvent[]): void {
    viewer?.destroy();
    viewer = null;
    container.replaceChildren();
    viewer = window.RiichiViewer.mount(container, {
        log,
        renderer: rendererSelect.value as "2d" | "3d",
    });
    currentLog = log;
}

async function loadInput(input: Uint8Array | string): Promise<void> {
    errorBanner.hidden = true;
    try {
        const { format, text } = await detectFormat(input);
        const { events, warnings } = PARSERS[format](text);
        const problems = validateEvents(events);
        if (warnings.length > 0 || problems.length > 0) {
            console.warn("mahjong-viewer: log issues", { warnings, problems });
        }
        mountViewer(events);
        const players = (
            events[0]?.type === "start_game" && Array.isArray(events[0].names)
                ? events[0].names.length
                : (events.find((e) => e.type === "start_kyoku")?.tehais as string[][] | undefined)
                      ?.length
        ) as number | undefined;
        const kyokus = events.filter((e) => e.type === "start_kyoku").length;
        const issues = warnings.length + problems.length;
        status.textContent =
            `${format} · ${players ?? "?"} players · ${kyokus} kyoku` +
            (issues > 0 ? ` · ${issues} warning${issues === 1 ? "" : "s"} (see console)` : "");
    } catch (e) {
        showError((e as Error).message);
    }
}

byId<HTMLButtonElement>("open-btn").addEventListener("click", () => fileInput.click());

fileInput.addEventListener("change", async () => {
    const file = fileInput.files?.[0];
    if (file) await loadInput(new Uint8Array(await file.arrayBuffer()));
    fileInput.value = "";
});

const loadPasted = async () => {
    const text = pasteInput.value.trim();
    if (text !== "") await loadInput(text);
};
byId<HTMLButtonElement>("load-btn").addEventListener("click", loadPasted);
pasteInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") void loadPasted();
});

rendererSelect.addEventListener("change", () => {
    if (currentLog) mountViewer(currentLog);
});

byId<HTMLButtonElement>("error-close").addEventListener("click", () => {
    errorBanner.hidden = true;
});

let dragDepth = 0;
window.addEventListener("dragenter", (e) => {
    e.preventDefault();
    dragDepth++;
    dropOverlay.hidden = false;
});
window.addEventListener("dragleave", () => {
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) dropOverlay.hidden = true;
});
window.addEventListener("dragover", (e) => e.preventDefault());
window.addEventListener("drop", async (e) => {
    e.preventDefault();
    dragDepth = 0;
    dropOverlay.hidden = true;
    const file = e.dataTransfer?.files?.[0];
    if (file) {
        await loadInput(new Uint8Array(await file.arrayBuffer()));
        return;
    }
    const text = e.dataTransfer?.getData("text");
    if (text) await loadInput(text);
});
